import { useEffect, useMemo, useRef, useState } from "react";
import { useAuthState } from "../../../auth/AuthStateProvider";
import { logConnectorFailure } from "../../../telemetry";
import { frequentPeople } from "../domain/people";
import { addDays, dayKeyOf, dayStart } from "../domain/time";
import { fetchCalendarView } from "../services/calendarView";
import type { Person } from "../services/people";
import type { CalEvent } from "../types";

/** The meetings the suggestions are ranked from: the last 30 days and the next 14. */
const DAYS_BACK = 30;
const DAYS_AHEAD = 14;

/** Read once per session: the ranking barely changes while the pane is open. */
let loaded: Promise<readonly CalEvent[]> | null = null;

/**
 * The people you work with, best first, for the add-attendee suggestions.
 * Empty until your recent meetings have loaded, or if they couldn't be read.
 */
export function useFrequentPeople(token: string | null, selfEmails: ReadonlySet<string>): readonly Person[] {
  const { refreshGraphToken } = useAuthState();
  const [events, setEvents] = useState<readonly CalEvent[]>([]);
  const tokenRef = useRef(token);
  tokenRef.current = token;
  const hasToken = Boolean(token);

  useEffect(() => {
    const current = tokenRef.current;
    if (!current) return;
    if (!loaded) {
      const today = dayKeyOf(new Date());
      loaded = fetchCalendarView(
        current,
        () => refreshGraphToken(false),
        dayStart(addDays(today, -DAYS_BACK)),
        dayStart(addDays(today, DAYS_AHEAD)),
      ).then((r) => r.events);
      // A failed read is tried again next time rather than kept.
      loaded.catch((e: unknown) => {
        loaded = null;
        logConnectorFailure("calendar", e, "load-frequent-people");
      });
    }
    let cancelled = false;
    loaded
      .then((list) => {
        if (!cancelled) setEvents(list);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [hasToken, refreshGraphToken]);

  return useMemo(() => frequentPeople(events, selfEmails), [events, selfEmails]);
}
